import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, CheckCircle, Clock, AlertCircle, User, Search, Edit2 } from 'lucide-react';
import { collection, addDoc, doc, setDoc, getDocs, updateDoc, arrayUnion, query, where } from "firebase/firestore";
import { NewCustomerModal } from './CustomerModals.js';
import useClickOutside from './hooks/useClickOutside.js';

const modalOverlayStyle = { position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: 'white', width: '500px', maxWidth:'95%', borderRadius: '8px', padding: '20px', display:'flex', flexDirection:'column', position: 'relative' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box', marginBottom: '15px' };
const btnPrimaryStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', maxHeight: '200px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1100 };
const dropdownItemStyle = { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };

const SALES_TEAM = [
    "Rajesh Soni", "Sanjay Kumawat", "Deepak Yadav", "Deepak Kumar", 
    "Rohit Soni", "Prinka Sharma", "Ankit Kumar Jangid"
];

export const TransactionModal = ({ show, onClose, onSave, db, transactionData = null }) => {
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        type: 'revenue',
        category: '',
        entityName: '', // New field for entity name
        amount: '',
        remark: ''
    });
    const [categories, setCategories] = useState([]);
    const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
    const [newCategory, setNewCategory] = useState('');

    useEffect(() => {
        const fetchCategories = async () => {
            const querySnapshot = await getDocs(collection(db, "accounts_categories"));
            const cats = querySnapshot.docs.map(doc => doc.data().name);
            setCategories(cats);
        };
        fetchCategories();
    }, [db]);

    useEffect(() => {
        if (transactionData) {
            setFormData({
                date: transactionData.date,
                type: transactionData.type,
                category: transactionData.category,
                entityName: transactionData.entityName || '', // Update for existing data
                amount: transactionData.amount,
                remark: transactionData.remark || ''
            });
        }
    }, [transactionData]);

    if (!show) return null;

    const handleSave = async () => {
        if (!formData.category && !newCategory) return alert("Please select or enter a category");
        if (!formData.amount) return alert("Please enter amount");
        if (!formData.entityName) return alert("Please enter entity name (e.g., vendor, customer)");

        const finalCategory = showNewCategoryInput ? newCategory : formData.category;

        try {
            const payload = {
                ...formData,
                category: finalCategory,
                amount: parseFloat(formData.amount),
                createdAt: new Date().toISOString()
            };
            // Decide whether to add or update based on transactionData presence
            if (transactionData) {
                await updateDoc(doc(db, "accounts_transactions", transactionData.id), payload);
            } else {
                await addDoc(collection(db, "accounts_transactions"), payload);
            }
            
            if (showNewCategoryInput) {
                if (!categories.includes(newCategory)) {
                    await addDoc(collection(db, "accounts_categories"), { name: newCategory });
                }
            }
            
            onSave();
            onClose();
        } catch (e) {
            alert("Error saving transaction: " + e.message);
        }
    };

    return (
        <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0 }}>{transactionData ? 'Edit' : 'Add'} Transaction</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X /></button>
                </div>

                <label style={labelStyle}>Date</label>
                <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={inputStyle} />

                <label style={labelStyle}>Type</label>
                <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} style={inputStyle}>
                    <option value="revenue">Revenue (Inflow)</option>
                    <option value="expense">Expense (Outflow)</option>
                </select>

                <div>
                    <label style={labelStyle}>Payable to *</label>
                    <input 
                        type="text"
                        value={formData.entityName} 
                        onChange={e => setFormData({...formData, entityName: e.target.value})} 
                        style={inputStyle} 
                        placeholder="Enter who this transaction is payable to"
                        autoComplete="off"
                    />
                </div>

                <label style={labelStyle}>Category</label>
                {!showNewCategoryInput ? (
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                        <select 
                            value={formData.category} 
                            onChange={e => setFormData({...formData, category: e.target.value})} 
                            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                        >
                            <option value="">Select Category</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button onClick={() => setShowNewCategoryInput(true)} style={{ padding: '0 10px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#f3f4f6' }}><Plus size={18}/></button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                        <input 
                            placeholder="New Category Name" 
                            value={newCategory} 
                            onChange={e => setNewCategory(e.target.value)} 
                            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                        />
                        <button onClick={() => setShowNewCategoryInput(false)} style={{ padding: '0 10px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#f3f4f6' }}><X size={18}/></button>
                    </div>
                )}

                <label style={labelStyle}>Amount</label>
                <input type="number" placeholder="0.00" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} style={inputStyle} />

                <label style={labelStyle}>Remark / Description</label>
                <textarea placeholder="Optional details..." value={formData.remark} onChange={e => setFormData({...formData, remark: e.target.value})} style={{ ...inputStyle, minHeight: '80px' }} />

                <button onClick={handleSave} style={btnPrimaryStyle}>Save Transaction</button>
            </div>
        </div>
    );
};

export const AccountsInvoiceModal = ({ show, onClose, onSave, db, invoiceData = null }) => {
    const [formData, setFormData] = useState({
        invoiceNo: '',
        customerID: '',
        customerName: '',
        salesPerson: '',
        totalAmount: '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        remark: ''
    });

    // --- CUSTOMER SEARCH STATE ---
    const [allCustomers, setAllCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showCustSuggestions, setShowCustSuggestions] = useState(false);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [showNewCustModal, setShowNewCustModal] = useState(false);
    const customerSearchRef = useClickOutside(() => setShowCustSuggestions(false));

    useEffect(() => {
        const fetchCustomers = async () => {
            const snap = await getDocs(collection(db, "customers"));
            setAllCustomers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };
        fetchCustomers();
    }, [db, show]);

    useEffect(() => {
        if (invoiceData) {
            setFormData({
                invoiceNo: invoiceData.invoiceNo,
                customerID: invoiceData.customerID || '',
                customerName: invoiceData.customerName,
                salesPerson: invoiceData.salesPerson,
                totalAmount: invoiceData.totalAmount,
                issueDate: invoiceData.issueDate,
                dueDate: invoiceData.dueDate,
                remark: invoiceData.remark || ''
            });
            setCustomerSearchTerm(invoiceData.customerName);
        }
    }, [invoiceData]);

    if (!show) return null;

    const handleCustomerSearch = (val) => {
        setCustomerSearchTerm(val);
        setFormData(prev => ({ ...prev, customerName: val })); // Keep name in sync for simplified cases
        if (val.length > 0) {
            const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(val.toLowerCase()));
            setFilteredCustomers(matches);
            setShowCustSuggestions(true);
        } else {
            setShowCustSuggestions(false);
        }
    };

    const selectCustomer = (cust) => {
        setFormData(prev => ({ ...prev, customerID: cust.id, customerName: cust.display_name }));
        setCustomerSearchTerm(cust.display_name);
        setShowCustSuggestions(false);
    };

    const handleSave = async () => {
        if (!formData.invoiceNo || !formData.customerName || !formData.totalAmount) {
            return alert("Please fill mandatory fields");
        }

        try {
            const payload = {
                ...formData,
                totalAmount: parseFloat(formData.totalAmount),
                amountPaid: invoiceData ? invoiceData.amountPaid : 0,
                balance: invoiceData ? (parseFloat(formData.totalAmount) - invoiceData.amountPaid) : parseFloat(formData.totalAmount),
                status: invoiceData ? invoiceData.status : 'pending',
                payments: invoiceData ? invoiceData.payments : [],
                createdAt: invoiceData ? invoiceData.createdAt : new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            if (invoiceData) {
                await updateDoc(doc(db, "accounts_invoices", invoiceData.id), payload);
            } else {
                await addDoc(collection(db, "accounts_invoices"), payload);
            }
            onSave();
            onClose();
        } catch (e) {
            alert("Error saving invoice");
        }
    };

    return (
        <>
            <div style={modalOverlayStyle}>
                <div style={{ ...modalContentStyle, width: '600px', overflow: 'visible' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0 }}>{invoiceData ? 'Edit' : 'New'} Simplified Invoice</h3>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X /></button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <label style={labelStyle}>Invoice Number *</label>
                            <input value={formData.invoiceNo} onChange={e => setFormData({...formData, invoiceNo: e.target.value})} style={inputStyle} />
                        </div>
                        <div style={{ position: 'relative' }} ref={customerSearchRef}>
                            <label style={labelStyle}>Customer Name *</label>
                            <input 
                                value={customerSearchTerm} 
                                onChange={e => handleCustomerSearch(e.target.value)} 
                                onFocus={() => customerSearchTerm && setShowCustSuggestions(true)}
                                style={inputStyle} 
                                placeholder="Search or type name..."
                                autoComplete="off"
                            />
                            {showCustSuggestions && (
                                <div style={dropdownStyle}>
                                    {filteredCustomers.map(cust => (
                                        <div key={cust.id} onClick={() => selectCustomer(cust)} style={dropdownItemStyle} className="hover-highlight">
                                            {cust.display_name}
                                        </div>
                                    ))}
                                    <div 
                                        onClick={() => { setShowNewCustModal(true); setShowCustSuggestions(false); }} 
                                        style={{ ...dropdownItemStyle, color: '#2563eb', fontWeight: 'bold', borderTop: '1px solid #e5e7eb' }}
                                    >
                                        + Add New Customer
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <label style={labelStyle}>Sales Person</label>
                            <select value={formData.salesPerson} onChange={e => setFormData({...formData, salesPerson: e.target.value})} style={inputStyle}>
                                <option value="">Select...</option>
                                {SALES_TEAM.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Total Amount *</label>
                            <input type="number" value={formData.totalAmount} onChange={e => setFormData({...formData, totalAmount: e.target.value})} style={inputStyle} />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <label style={labelStyle}>Issue Date</label>
                            <input type="date" value={formData.issueDate} onChange={e => setFormData({...formData, issueDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Due Date</label>
                            <input type="date" value={formData.dueDate} onChange={e => setFormData({...formData, dueDate: e.target.value})} style={inputStyle} />
                        </div>
                    </div>

                    <label style={labelStyle}>Remark</label>
                    <textarea value={formData.remark} onChange={e => setFormData({...formData, remark: e.target.value})} style={{ ...inputStyle, minHeight: '60px' }} />

                    <button onClick={handleSave} style={btnPrimaryStyle}>Save Invoice</button>
                </div>
            </div>

            <NewCustomerModal 
                show={showNewCustModal} 
                onClose={() => setShowNewCustModal(false)} 
                onSave={(cust) => {
                    setAllCustomers([...allCustomers, cust]);
                    selectCustomer(cust);
                    setShowNewCustModal(false);
                }}
                db={db}
                isMobile={false}
            />
        </>
    );
};

export const PaymentModal = ({ show, onClose, onSave, db, invoice }) => {
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [method, setMethod] = useState('Bank Transfer');
    const [remark, setRemark] = useState('');

    if (!show || !invoice) return null;

    const handleRecord = async () => {
        const payAmount = parseFloat(amount);
        if (!payAmount || payAmount <= 0) return alert("Enter valid amount");
        if (payAmount > invoice.balance) return alert("Amount exceeds remaining balance");

        try {
            const newPaid = (invoice.amountPaid || 0) + payAmount;
            const newBalance = invoice.totalAmount - newPaid;
            let newStatus = 'pending';
            if (newBalance <= 0) newStatus = 'paid';
            else if (newPaid > 0) newStatus = 'partial';

            const paymentObj = {
                amount: payAmount,
                date,
                method,
                remark,
                recordedAt: new Date().toISOString()
            };

            await updateDoc(doc(db, "accounts_invoices", invoice.id), {
                amountPaid: newPaid,
                balance: newBalance,
                status: newStatus,
                payments: arrayUnion(paymentObj)
            });

            // Add a corresponding revenue transaction
            const transactionPayload = {
                date: date,
                type: 'revenue',
                category: 'Invoice Payment', // Default category for invoice payments
                amount: payAmount,
                remark: `Payment for Invoice ${invoice.invoiceNo} from ${invoice.customerName}`,
                createdAt: new Date().toISOString()
            };
            await addDoc(collection(db, "accounts_transactions"), transactionPayload);
            
            // Ensure 'Invoice Payment' category exists in accounts_categories
            const categoriesRef = collection(db, "accounts_categories");
            const q = query(categoriesRef, where("name", "==", "Invoice Payment"));
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) {
                await addDoc(categoriesRef, { name: "Invoice Payment" });
            }


            onSave();
            onClose();
        } catch (e) {
            alert("Error recording payment");
        }
    };

    return (
        <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0 }}>Record Payment</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X /></button>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Invoice: {invoice.invoiceNo}</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Pending: ₹{invoice.balance?.toLocaleString('en-IN')}</div>
                </div>

                <label style={labelStyle}>Amount Received</label>
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" style={inputStyle} />

                <label style={labelStyle}>Payment Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />

                <label style={labelStyle}>Payment Method</label>
                <select value={method} onChange={e => setMethod(e.target.value)} style={inputStyle}>
                    <option>Bank Transfer</option>
                    <option>UPI / GPay</option>
                    <option>Cash</option>
                    <option>Cheque</option>
                </select>

                <label style={labelStyle}>Notes</label>
                <textarea value={remark} onChange={e => setRemark(e.target.value)} style={{ ...inputStyle, minHeight: '60px' }} />

                <button onClick={handleRecord} style={btnPrimaryStyle}>Confirm Payment</button>
            </div>
        </div>
    );
};
